// src/pages/Login.js

import React, { useState, useContext, useEffect } from 'react';
import {
  Button,
  Form,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/svg/gptw_logo_emp.svg';
import { login } from '../services/authService';
import { AuthContext } from '../contexts/AuthContext';
import axiosInstance from '../axiosInstance';
import './Login.css';

// ----------------------------------------------------------------
// Forgot Password Modal Component
// ----------------------------------------------------------------
const ForgotPasswordModal = ({ show, handleClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('success');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlertMessage(null);

    try {
      await axiosInstance.post('/api/forgot-password/', { email });
      setAlertVariant('success');
      setAlertMessage(
        "Wenn diese E-Mail existiert, erhältst Du eine Anleitung zum Zurücksetzen des Passworts."
      );
    } catch (error) {
      setAlertVariant('danger');
      setAlertMessage(
        error.response?.data?.message ||
        'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="login-modal">
      <Modal.Header closeButton>
        <Modal.Title>Passwort vergessen?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {alertMessage && <Alert variant={alertVariant}>{alertMessage}</Alert>}
        <Form onSubmit={handleForgotPassword}>
          <Form.Group controlId="forgotPasswordEmail" className="mb-3">
            <Form.Label className="login-form-label">
              Gebe Deine E-Mail-Adresse ein
            </Form.Label>
            <Form.Control
              type="email"
              placeholder="mail@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-form-input"
            />
          </Form.Group>
          <Button type="submit" className="login-submit-button" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Senden...
              </>
            ) : (
              'Link senden'
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// ----------------------------------------------------------------
// Main Login Page Component
// ----------------------------------------------------------------
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { loginUser } = useContext(AuthContext);

  useEffect(() => {
    document.title = 'TRAC - Login';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = await login(email, password);
      loginUser(data.token, data.user?.id ?? data.user_id ?? null);
      navigate('/home');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="login-container d-flex flex-column p-0">
      {/* Decorative red squares - 2x2 grid layout */}
      <div className="login-decorative-grid">
        <div className="login-decorative-square-1" />
        <div className="login-decorative-square-2" />
      </div>

      <Row className="flex-grow-1 g-0 login-row">
        {/* Left Column - Login Form */}
        <Col lg={7} md={12} className="login-left-column">
          <div className="login-content-wrapper">
            {/* Main Title */}
            <div style={{ maxWidth: '868px' }}>
              <h1 className="login-main-title">
                Willkommen in TRAC<sup>®</sup>- Deinem Tool im Folgeprozess von Mitarbeiter-Befragungen
              </h1>
              
              {/* Subtitle */}
              <p className="login-subtitle">
                Melde Dich mit Deinen Zugangsdaten an.
              </p>
            </div>

            {/* Login Form */}
            <Form onSubmit={handleSubmit} className="login-form">
              {/* Email Field */}
              <Form.Group controlId="formBasicEmail" className="mb-4">
                <Form.Label className="login-form-label">E-Mail-Adresse</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@beispiel.de"
                  className="login-form-input"
                />
              </Form.Group>

              {/* Password Field */}
              <Form.Group controlId="formBasicPassword" className="mb-3">
                <Form.Label className="login-form-label">Passwort</Form.Label>
                <div className="login-password-group">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="********************"
                    className="login-form-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="login-password-toggle"
                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  >
                    {showPassword ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </Form.Group>

              {/* Error Alert */}
              {error && (
                <Alert variant="danger" className="login-error-alert">
                  {error}
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="login-submit-button"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Anmelden...
                  </>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </Form>

            {/* Forgot Password Link */}
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="login-forgot-password"
              tabIndex={0}
            >
              Passwort vergessen?
            </button>
          </div>

          {/* Footer */}
          <footer className="login-footer">
            <p className="login-footer-text">
              2026 © Great Place To Work® Institute Inc. All Rights Reserved.
            </p>
          </footer>
        </Col>

        {/* Right Column - Logo Panel */}
        <Col lg={5} className="login-right-column">
          <img
            src={logo}
            alt="Great Place To Work logo"
            className="login-logo"
          />
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        show={showForgotPassword}
        handleClose={() => setShowForgotPassword(false)}
      />
    </Container>
  );
};

export default Login;
